import { useMemo, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { Link, Navigate } from "react-router-dom";
import WorldMapHero from "@/components/WorldMapHero";
import { trackNudge } from "@/lib/analytics";
import { GoogleSignInButton, OrDivider } from "@/components/GoogleSignInButton";

export default function Register() {
  const { user, register, error, setError } = useAuth();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);

  // Where the visitor came from (set by the public-page signup nudge)
  const signupContext = useMemo(() => {
    try {
      return JSON.parse(sessionStorage.getItem("pins:signup-context") || "null");
    } catch (e) {
      void e;
      return null;
    }
  }, []);

  if (user) return <Navigate to="/" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    const ok = await register(name, email, password, acceptedTerms);
    if (ok !== false) {
      localStorage.setItem("pins:show-onboarding", "1");
      // Attribute the signup to the nudge it came from (fire-and-forget)
      try {
        const attr = JSON.parse(sessionStorage.getItem("pins:nudge-attribution") || "null");
        if (attr) {
          trackNudge("signup", attr.variant, attr.token);
          sessionStorage.removeItem("pins:nudge-attribution");
        }
      } catch (err) { void err; }
    }
    setSubmitting(false);
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="flex items-center justify-center p-8 lg:p-16 bg-[#f7f5f0] order-2 lg:order-1">
        <form onSubmit={submit} className="w-full max-w-md" data-testid="register-form">
          <p className="overline">Begin</p>
          <h2 className="font-serif text-4xl lg:text-5xl leading-none mt-3">Open a new atlas.</h2>
          <p className="mt-4 text-[#5c5c5c]">A journal for every place worth remembering.</p>
          {signupContext?.ownerName && (
            <p
              className="mt-3 font-mono text-[11px] uppercase tracking-[0.15em] text-[#2c4a3e] border-l-2 border-[#b85c47] pl-3"
              data-testid="signup-context-line"
            >
              You were exploring {signupContext.ownerName}'s
              {signupContext.tripName ? ` "${signupContext.tripName}"` : " journey"} — time to chart your own.
            </p>
          )}

          <div className="mt-10 space-y-6">
            <GoogleSignInButton label="Sign up with Google" />
            <p className="-mt-3 font-mono text-[10px] uppercase tracking-[0.15em] text-[#8a8a8a]" data-testid="google-terms-note">
              By continuing with Google you agree to our Terms and Privacy Policy.
            </p>
            <OrDivider />
            <div>
              <label className="field-label" htmlFor="name">Name</label>
              <input id="name" data-testid="register-name-input" type="text" required className="field-input" value={name} onChange={(e) => setName(e.target.value)} />
            </div>
            <div>
              <label className="field-label" htmlFor="email">Email</label>
              <input id="email" data-testid="register-email-input" type="email" required className="field-input" value={email} onChange={(e) => setEmail(e.target.value)} />
            </div>
            <div>
              <label className="field-label" htmlFor="password">Password</label>
              <input id="password" data-testid="register-password-input" type="password" required minLength={6} className="field-input" value={password} onChange={(e) => setPassword(e.target.value)} />
            </div>

            {error && (
              <div data-testid="register-error" className="border border-[#b85c47] px-3 py-2 text-sm font-mono text-[#963f2b]">
                {error}
              </div>
            )}

            <label className="flex items-start gap-2.5 text-sm text-[#5c5c5c] cursor-pointer" data-testid="register-terms-label">
              <input
                type="checkbox"
                required
                checked={acceptedTerms}
                onChange={(e) => setAcceptedTerms(e.target.checked)}
                data-testid="register-terms-checkbox"
                className="mt-0.5 accent-[#2c4a3e]"
              />
              <span>
                I agree to the{" "}
                <Link to="/terms" target="_blank" className="underline underline-offset-2 text-[#2c4a3e]" data-testid="register-terms-link">
                  Terms of Service
                </Link>{" "}
                and{" "}
                <Link to="/privacy" target="_blank" className="underline underline-offset-2 text-[#2c4a3e]" data-testid="register-privacy-link">
                  Privacy Policy
                </Link>
                .
              </span>
            </label>

            <button data-testid="register-submit-button" className="btn-primary w-full" disabled={submitting || !acceptedTerms}>
              {submitting ? "Creating…" : "Create journal"}
            </button>
          </div>

          <div className="mt-8 flex items-center justify-between font-mono text-xs uppercase tracking-[0.2em] text-[#5c5c5c]">
            <span>Already exploring?</span>
            <Link to="/login" data-testid="login-link" className="underline underline-offset-4 text-[#2c4a3e]">
              Sign in →
            </Link>
          </div>
        </form>
      </div>
      <div className="relative hidden lg:block order-1 lg:order-2">
        <WorldMapHero />
        <div className="relative z-10 h-full flex items-end p-12 text-[#f7f5f0]">
          <div>
            <p className="overline text-[#f7f5f0]/80">Prologue</p>
            <h1 className="font-serif text-5xl lg:text-6xl leading-none mt-3 max-w-md">
              A private map for the places that shaped you.
            </h1>
          </div>
        </div>
        <div className="absolute top-12 left-12 flex items-center gap-3 text-[#f7f5f0]">
          <img src="/logo.svg" alt="Pins" className="w-9 h-9" />
          <span className="font-serif text-2xl">Pins</span>
        </div>
      </div>
    </div>
  );
}
